import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  spaceInvitesQueryOptions,
  createInvite,
  revokeInvite,
} from "./spaces-queries";
import type { SpaceUiType } from "./types";

/**
 * Shared building blocks for the create/edit space dialogs. The two
 * dialogs intentionally stay separate (membership/invite endpoints
 * require the space to exist), but they render the same fields, so
 * the radio set and the invite-link row live here.
 */

const TYPE_OPTIONS: ReadonlyArray<{
  v: SpaceUiType;
  title: string;
  description: string;
  adminOnly?: boolean;
}> = [
  {
    v: "personal",
    title: "Личное",
    description: "Только вы. Сессии и результаты не видны никому.",
  },
  {
    v: "team_private",
    title: "Командное приватное",
    description: "Ресурсы пространства доступны коллегам, но каждый работает в своей сессии.",
  },
  {
    v: "team_shared",
    title: "Командное общее",
    description: "Все участники видят сессии и результаты друг друга.",
  },
  {
    v: "public",
    title: "Публичное",
    description: "Видно всем сотрудникам организации; общие агенты и коннекторы, чаты у каждого свои.",
    adminOnly: true,
  },
];

export function SpaceTypeRadios({
  value,
  onChange,
  isAdmin,
  disabled = false,
}: {
  value: SpaceUiType;
  onChange: (v: SpaceUiType) => void;
  isAdmin: boolean;
  /** Lock all radios — used for non-owner viewers in the edit dialog. */
  disabled?: boolean;
}) {
  return (
    <div className="flex flex-col gap-2">
      {TYPE_OPTIONS.map((opt) => {
        const checked = value === opt.v;
        const lockedByAdmin = !!opt.adminOnly && !isAdmin;
        const isDisabled = disabled || lockedByAdmin;
        return (
          <button
            key={opt.v}
            type="button"
            disabled={isDisabled}
            onClick={() => onChange(opt.v)}
            className={`flex w-full items-start gap-3 rounded-2xl border px-4 py-3 text-left ${
              checked ? "border-black/40" : "border-black/10"
            } ${isDisabled ? "opacity-60 cursor-not-allowed" : "hover:border-black/20"}`}
          >
            <span
              className={`mt-1 inline-block h-3.5 w-3.5 rounded-full border ${
                checked ? "border-black bg-black" : "border-black/30"
              }`}
            />
            <span className="flex-1">
              <span className="block font-medium text-black/90">{opt.title}</span>
              <span className="block text-sm text-black/55">
                {opt.description}
                {lockedByAdmin ? " (только для администраторов)" : ""}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * Invite-link row: shows the active invite URL (first non-revoked one)
 * with a copy button, plus an optional revoke button and an optional
 * one-shot auto-create on mount. Doesn't decide visibility — the parent
 * gates on owner role and non-organization access mode.
 *
 * - `autoCreate`: fire `createInvite` once after the invites query loads
 *   if there's nothing active. Used by the create dialog's step 2 so the
 *   user lands directly on a copyable link.
 * - `showRevoke`: hide the revoke button (e.g. on step 2 of creation,
 *   where revoking the link you just made is nonsensical).
 */
export function InviteLinkRow({
  spaceId,
  autoCreate = false,
  showRevoke = true,
}: {
  spaceId: string;
  autoCreate?: boolean;
  showRevoke?: boolean;
}) {
  const qc = useQueryClient();
  const { data: invites } = useQuery(spaceInvitesQueryOptions(spaceId));
  const inviteMutation = useMutation({
    mutationFn: () => createInvite(spaceId, { role: "editor" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["spaces", spaceId, "invites"] }),
  });
  const revokeMutation = useMutation({
    mutationFn: (token: string) => revokeInvite(spaceId, token),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["spaces", spaceId, "invites"] }),
  });

  const activeInvite = (invites ?? []).find((i) => !i.revoked_at);
  const inviteUrl = activeInvite ? `${window.location.origin}/invite/${activeInvite.token}` : null;

  // One-shot auto-create. Guarded by a ref so a refetch of the invites
  // list (e.g. on focus) can't kick off a second POST after we already
  // tried, and so the user manually revoking a freshly auto-created link
  // doesn't trigger an immediate re-create loop.
  const autoCreatedRef = useRef(false);
  const { mutate: createOne } = inviteMutation;
  useEffect(() => {
    if (!autoCreate || autoCreatedRef.current) return;
    if (invites === undefined) return;
    autoCreatedRef.current = true;
    if (!activeInvite) createOne();
  }, [autoCreate, invites, activeInvite, createOne]);

  const [copied, setCopied] = useState(false);
  const copy = () => {
    if (!inviteUrl) return;
    void navigator.clipboard.writeText(inviteUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  const placeholder =
    autoCreate && (invites === undefined || inviteMutation.isPending)
      ? "Создаём ссылку…"
      : "Ссылка ещё не создана";

  return (
    <div className="flex gap-2">
      <div className="flex-1 truncate rounded-xl border border-black/10 bg-black/[0.02] px-3 py-2.5 text-sm text-black/60">
        {inviteUrl ?? placeholder}
      </div>
      {inviteUrl ? (
        <>
          <button
            type="button"
            onClick={copy}
            className="rounded-xl border border-black/10 px-4 py-2.5 text-sm hover:border-black/30"
          >
            {copied ? "Скопировано" : "Копировать"}
          </button>
          {showRevoke && (
            <button
              type="button"
              onClick={() => revokeMutation.mutate(activeInvite!.token)}
              className="rounded-xl border border-black/10 px-3 py-2.5 text-sm text-red-600 hover:border-red-200"
              title="Отозвать ссылку"
            >
              Отозвать
            </button>
          )}
        </>
      ) : autoCreate ? null : (
        <button
          type="button"
          onClick={() => inviteMutation.mutate()}
          disabled={inviteMutation.isPending}
          className="rounded-xl bg-black px-4 py-2.5 text-sm text-white hover:bg-black/85 disabled:bg-black/30"
        >
          Создать
        </button>
      )}
    </div>
  );
}
